// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { HistoryType } from '../History/HistoryType';
import { ProductType } from './ProductType';
import { InstrumentTypes, InstrumentTypesColor, getInstrumentTypeShortName } from './InstrumentTypes';
import { LOCALE_EN, Resources } from '@shared/localizations/Resources';
import { MathUtils } from '../MathUtils';
import { TradingMode } from './TradingMode';
import { PriceFormatter } from './PriceFormatter';
import { StringUtils } from '../StringUtils';
import { ticksUtils } from '@shared/utils/Trading/TicksUtils';
import { BaseSettings } from '@shared/commons/Settings/BaseGeneralSettingsWrapper';
import { OffsetModeViewEnum } from '../Trading/OffsetModeViewEnum';
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
class _InstrumentUtils {
    constructor() {
        this.MAX_PRECISION_ON_SERVER = 8; // #46163  - уточнил у Хоменко, база данных на сервере не поддерживает большую точность цены , для js взял с клиента
        this.SEPARATOR = ':';
        this.GENERAL_STRING = 'General';
        this._useFuturesInsteadSpot = null;
    }
    OpenSpread(SpreadPlan, instrument) {
        let spreadItem = null;
        if (!isNullOrUndefined(SpreadPlan) && SpreadPlan.IsNotEmptyPlan(instrument)) {
            spreadItem = SpreadPlan.GetItem(instrument);
        }
        if (spreadItem !== null) {
            if (instrument.HistoryType === HistoryType.QUOTE_LEVEL1) {
                return spreadItem.CalcBid(instrument.BidOpen, instrument.AskOpen, instrument);
            }
            else if (instrument.HistoryType === HistoryType.QUOTE_ASK) {
                return spreadItem.CalcAsk(instrument.BidOpen, instrument.AskOpen, instrument);
            }
        }
        return instrument.Open;
    }
    HighSpread(SpreadPlan, instrument) {
        let spreadItem = null;
        if (!isNullOrUndefined(SpreadPlan) && SpreadPlan.IsNotEmptyPlan(instrument)) {
            spreadItem = SpreadPlan.GetItem(instrument);
        }
        if (spreadItem !== null) {
            if (instrument.HistoryType === HistoryType.QUOTE_LEVEL1) {
                return spreadItem.CalcBid(instrument.BidHigh, instrument.AskHigh, instrument);
            }
            else if (instrument.HistoryType === HistoryType.QUOTE_ASK) {
                return spreadItem.CalcAsk(instrument.BidHigh, instrument.AskHigh, instrument);
            }
        }
        return instrument.High;
    }
    LowSpread(SpreadPlan, instrument) {
        let spreadItem = null;
        if (!isNullOrUndefined(SpreadPlan) && SpreadPlan.IsNotEmptyPlan(instrument)) {
            spreadItem = SpreadPlan.GetItem(instrument);
        }
        if (spreadItem !== null) {
            if (instrument.HistoryType === HistoryType.QUOTE_LEVEL1) {
                return spreadItem.CalcBid(instrument.BidLow, instrument.AskLow, instrument);
            }
            else if (instrument.HistoryType === HistoryType.QUOTE_ASK) {
                return spreadItem.CalcAsk(instrument.BidLow, instrument.AskLow, instrument);
            }
        }
        return instrument.Low;
    }
    PrevCloseSpread(SpreadPlan, instrument) {
        let spreadItem = null;
        if (!isNullOrUndefined(SpreadPlan) && SpreadPlan.IsNotEmptyPlan(instrument)) {
            spreadItem = SpreadPlan.GetItem(instrument);
        }
        if (spreadItem !== null) {
            if (instrument.HistoryType === HistoryType.QUOTE_LEVEL1) {
                return spreadItem.CalcBid(instrument.BidPrevClose, instrument.AskPrevClose, instrument);
            }
            else if (instrument.HistoryType === HistoryType.QUOTE_ASK) {
                return spreadItem.CalcAsk(instrument.BidPrevClose, instrument.AskPrevClose, instrument);
            }
        }
        return instrument.Close;
    }
    Get_Ticks_From_RealOffsetPrice(instrument, absoluteOffset, optionsVariousPipsSize = -1) {
        if (instrument == null) {
            return absoluteOffset;
        }
        if (instrument.PointSize === 0) {
            return 0.0;
        }
        if (optionsVariousPipsSize === -1 || optionsVariousPipsSize === 0) {
            optionsVariousPipsSize = instrument.PointSize;
        }
        return Number((absoluteOffset / optionsVariousPipsSize).toFixed(0)); // именно пипс тут! так правильно только для форекс
    }
    GetFullName(symbol, route) {
        if (!symbol)
            return '';
        return !route || symbol.indexOf(InstrumentUtils.SEPARATOR) !== -1
            ? symbol
            : symbol + InstrumentUtils.SEPARATOR + route;
    }
    ParseRouteName(symbol) {
        if (!symbol) {
            return '';
        }
        const index = symbol.lastIndexOf(InstrumentUtils.SEPARATOR);
        return index >= 0 ? symbol.substr(index + InstrumentUtils.SEPARATOR.length) : '';
    }
    RemoveRouteName(symbol) {
        if (!symbol) {
            return null;
        }
        const index = symbol.lastIndexOf(InstrumentUtils.SEPARATOR);
        return index >= 0 ? symbol.substr(0, index) : symbol;
    }
    IsFullName(name) {
        return name !== null && name.lastIndexOf(InstrumentUtils.SEPARATOR) > -1;
    }
    GetFullNameUnsafe(symbol, route) {
        return symbol + InstrumentUtils.SEPARATOR + route;
    }
    // TODO. Ugly.
    DivideFullName(symbol) {
        let name = null;
        let route = null;
        const index = symbol.lastIndexOf(InstrumentUtils.SEPARATOR);
        if (index === -1) {
            name = symbol;
            route = '';
        }
        else {
            name = symbol.substr(0, index);
            route = symbol.substr(index + 1);
        }
        return { name, route };
    }
    getAllowedProductTypeDict(instrument) {
        if (!instrument) {
            return [];
        }
        return instrument.RiskSettings.RiskPlanSettings.availableProductTypes;
    }
    GetLocalizedProductType(instrument, productType) {
        if (!instrument)
            return '';
        if (productType == ProductType.Delivery &&
            (instrument.isFutureOrOption() ||
                (instrument.InstrType && instrument.InstrType === InstrumentTypes.CFD_FUTURES))) {
            return Resources.getResource('ProductType.CarryForward');
        }
        return Resources.getResource('ProductType.' + ProductType[productType]);
    }
    getInstrumentTypeStringLocalized(type, cfd = undefined) {
        if (cfd) {
            return Resources.getResource('panel.TerceraSymbolLookupDropDownForm.CFDs');
        }
        switch (type) {
            case -1:
                return Resources.getResource('panel.TerceraSymbolLookupDropDownForm.All');
            case InstrumentTypes.FUTURES:
                return Resources.getResource('panel.TerceraSymbolLookupDropDownForm.Futures');
            case InstrumentTypes.FOREX:
                return Resources.getResource('panel.TerceraSymbolLookupDropDownForm.Forex');
            case InstrumentTypes.EQUITIES:
                return Resources.getResource('panel.TerceraSymbolLookupDropDownForm.Equities');
            case InstrumentTypes.OPTIONS:
                return Resources.getResource('panel.TerceraSymbolLookupDropDownForm.Options');
            case InstrumentTypes.CFD_FUTURES:
            case InstrumentTypes.EQUITIES_CFD:
                return Resources.getResource('panel.TerceraSymbolLookupDropDownForm.CFDs');
            case InstrumentTypes.INDICIES:
                return Resources.getResource('panel.TerceraSymbolLookupDropDownForm.indices');
            case InstrumentTypes.CRYPTO:
                return Resources.getResource('panel.TerceraSymbolLookupDropDownForm.crypto');
            case InstrumentTypes.PORTFOLIO:
                return Resources.getResource('panel.TerceraSymbolLookupDropDownForm.Portfolio');
            case InstrumentTypes.INS_HISTORY:
                return Resources.getResource('general.CUSTOM');
            case InstrumentTypes.SPREADBET:
                return Resources.getResource('panel.TerceraSymbolLookupDropDownForm.Spreadbet');
            case InstrumentTypes.FIXED_INCOME:
                return Resources.getResource('panel.TerceraSymbolLookupDropDownForm.FixedIncome');
            case InstrumentTypes.ETF:
                return Resources.getResource('panel.TerceraSymbolLookupDropDownForm.ETFs');
            case InstrumentTypes.TBILL:
                return Resources.getResource('panel.TerceraSymbolLookupDropDownForm.TBill');
            case InstrumentTypes.SPOT:
                return InstrumentUtils.UseFuturesInsteadSpot()
                    ? Resources.getResource('panel.TerceraSymbolLookupDropDownForm.Futures')
                    : Resources.getResource('panel.TerceraSymbolLookupDropDownForm.Spot');
            case InstrumentTypes.FORWARD:
                return Resources.getResource('panel.TerceraSymbolLookupDropDownForm.Forward');
            case InstrumentTypes.CORPORATE:
                return Resources.getResource('panel.TerceraSymbolLookupDropDownForm.Corporate');
            case InstrumentTypes.FUND:
                return Resources.getResource('panel.TerceraSymbolLookupDropDownForm.Fund');
            default:
                return InstrumentUtils.GENERAL_STRING;
        }
    }
    getPutCallOptionsTypeStringLocalized(instrument) {
        // special Option type string for WatchList's Group By #114165
        if (!instrument) {
            return null;
        }
        return instrument.isOptionSymbol
            ? Resources.getResource(instrument.PutCall
                ? 'panel.TerceraSymbolLookupDropDownForm.Options.Put'
                : 'panel.TerceraSymbolLookupDropDownForm.Options.Call')
            : instrument.getTypeString();
    }
    UseFuturesInsteadSpot() {
        if (!InstrumentUtils._useFuturesInsteadSpot) {
            InstrumentUtils._useFuturesInsteadSpot =
                Resources.getResourceLang('panel.TerceraSymbolLookupDropDownForm.Spot', LOCALE_EN) === 'Futures';
        }
        return InstrumentUtils._useFuturesInsteadSpot;
    }
    UseFuturesInsteadSpotShortName() {
        return InstrumentUtils.UseFuturesInsteadSpot()
            ? getInstrumentTypeShortName(InstrumentTypes.FUTURES)
            : getInstrumentTypeShortName(InstrumentTypes.SPOT);
    }
    UseFuturesInsteadSpotColor() {
        return InstrumentUtils.UseFuturesInsteadSpot()
            ? InstrumentTypesColor[InstrumentTypes.FUTURES]
            : InstrumentTypesColor[InstrumentTypes.SPOT];
    }
    ConvertPointsToTicks(instrument, offsetInPoints) {
        if (!(instrument === null || instrument === void 0 ? void 0 : instrument.PointSize)) {
            return null;
        }
        return offsetInPoints / instrument.PointSize;
    }
    GetTradingStatus(instrument) {
        if (!instrument) {
            return StringUtils.NOT_AVAILABLE;
        }
        const route = instrument ? instrument.DataCache.Routes.getRouteByName(instrument.getRoute()) : null;
        if (route == null || !route.IsTradable) {
            return (Resources.getResource('InstrumentDetailsPanel.Closed') +
                ' ' +
                Resources.getResource('InstrumentDetailsPanel.(Indicative symbol)'));
        }
        else if (instrument.TradingMode === TradingMode.FullyOpen) {
            return (Resources.getResource('InstrumentDetailsPanel.Open') +
                (instrument.InstrType === InstrumentTypes.INDICIES
                    ? ' ' + Resources.getResource('InstrumentDetailsPanel.(Indicative symbol)')
                    : ''));
        }
        else if (instrument.TradingMode === TradingMode.TradingHalt) {
            return Resources.getResource('InstrumentDetailsPanel.TradingHalt');
        }
        else if (instrument.TradingMode === TradingMode.LiquidationOnly) {
            return Resources.getResource('InstrumentDetailsPanel.LiquidationOnly');
        }
        else if (instrument.TradingMode === TradingMode.CAMaintenance) {
            return Resources.getResource('InstrumentDetailsPanel.CAMaintenance');
        }
        else {
            return Resources.getResource('InstrumentDetailsPanel.Closed');
        }
    }
    formatAmountValue(value, instrument, account, productType = ProductType.General) {
        if (instrument == null) {
            return PriceFormatter.formatPrice(value, 2);
        }
        const prec = instrument.getLotStepPrecision(productType, account);
        if (value >= 1 && MathUtils.trunc(value) === value) {
            const lotStep = instrument.getLotStep(productType, account);
            if (MathUtils.trunc(lotStep) === lotStep) {
                // N0 format.
                return PriceFormatter.formatPrice(value, 0);
            }
            else {
                return PriceFormatter.formatPrice(value, prec);
            }
        }
        else {
            return PriceFormatter.formatPrice(value, prec);
        }
    }
    getFormatPrice(instrument, price, NAString) {
        if (isNaN(price)) {
            // || price === -1)   // #87569
            return NAString;
        }
        return instrument ? instrument.formatPrice(price) : price.toFixed(4);
    }
    getFormatVolume(instrument, volume, showLots = false, NAString = null, productType = ProductType.General, account = null) {
        NAString = NAString !== null && NAString !== void 0 ? NAString : StringUtils.NOT_AVAILABLE;
        if (isNaN(volume) || instrument == null) {
            return NAString;
        }
        return instrument.DataCache.formatVolume(instrument, instrument.getDoubleVolume(volume, showLots), showLots, productType, account);
    }
    ParseSearchPattern(pattern) {
        if (!isValidString(pattern)) {
            return pattern;
        }
        if (pattern.startsWith('[')) {
            pattern = pattern.substring(1);
        }
        if (pattern.endsWith(']')) {
            pattern = pattern.substring(0, pattern.length - 1);
        }
        return pattern;
    }
    tickValue(instrument, lastPrice, openPrice, signOper) {
        if (openPrice === 0 || Number.isNaN(lastPrice) || Number.isNaN(openPrice)) {
            return 0;
        }
        let ticks = 0;
        try {
            const offset = lastPrice - openPrice;
            if (!isNullOrUndefined(instrument) && instrument.VariableTickList.length > 1) {
                if (instrument.PointSize === 0) {
                    ticks = (lastPrice - openPrice) * signOper;
                }
                else {
                    ticks = instrument.calculateTicks(openPrice, offset) * signOper * (offset > 0 ? 1 : -1);
                }
            }
            else {
                ticks = offset * signOper;
            }
            if (!isNullOrUndefined(instrument) && ticksUtils.AllowUseFractinalTicksForForex(instrument.InstrType)) {
                const presision = instrument.getPrecision();
                if ((presision && presision % 2) !== 0 &&
                    BaseSettings.offsetType === OffsetModeViewEnum.TicksFractionalForForex) // #38563 - UseFractinalTicksForForex
                 {
                    ticks = MathUtils.Round(ticks, 1);
                }
                else {
                    ticks = MathUtils.Round(ticks, 0);
                }
            }
            else if (instrument === null || instrument === void 0 ? void 0 : instrument.isOptionSymbol) {
                ticks = MathUtils.Round(ticks, 3);
            }
            else {
                ticks = MathUtils.Round(ticks, 0);
            }
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
        }
        return ticks;
    }
}
export const InstrumentUtils = new _InstrumentUtils();
